import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getTypeSamples } from '../../../services/list.services';
import { getTypeSampleSourcesList } from '../../../services/typeSampleSource.services';
import { SchemeModes, SchemeNodeTypes, SchemeObjectTypes } from '../../../consts/scheme.consts';

const SchemeEditorContext = createContext(null);

export const useSchemeEditorContext = () => {
  const context = useContext(SchemeEditorContext);
  if (!context) {
    throw new Error('useSchemeEditorContext must be used within a SchemeEditorProvider');
  }
  return context;
};

export const SchemeEditorProvider = ({
  children,
  initialNodes,
  mode = SchemeModes.view,
  useStatusIndication = false,
  loadingDeviceType,
}) => {
  const [nodes, setNodes] = useState([]);
  const [typeSamplesList, setTypeSamplesList] = useState([]);
  const [typeSampleSourcesList, setTypeSampleSourcesList] = useState([]);
  const [schemeMode, setSchemeMode] = useState(mode ?? SchemeModes.view);

  useEffect(() => {
    loadDictionaries();
  }, []);

  useEffect(() => {
    setNodes(initialNodes);
    setSchemeMode(mode);
  }, [initialNodes, mode]);

  const loadDictionaries = async () => {
    const [typeSamplesResponse, typeSampleSourcesResponse] = await Promise.all([
      getTypeSamples(),
      getTypeSampleSourcesList(),
    ]);

    const typeSamples = typeSamplesResponse?.map((item) => ({
      title: item.name,
      type: SchemeObjectTypes.typeSamples,
      node: SchemeNodeTypes.sample,
      value: item.id,
    }));
    const typeSampleSources = typeSampleSourcesResponse.result?.data.map((item) => ({
      title: item.name,
      type: SchemeObjectTypes.typeSampleSources,
      node: SchemeNodeTypes.sample,
      value: item.id,
    }));
    setTypeSamplesList(typeSamples);
    setTypeSampleSourcesList(typeSampleSources);
  };

  const value = useMemo(() => {
    return {
      nodes,
      setNodes,
      typeSamplesList,
      typeSampleSourcesList,
      schemeMode,
      useStatusIndication,
      loadingDeviceType,
    };
  }, [nodes, typeSamplesList, typeSampleSourcesList, schemeMode, useStatusIndication]);

  return <SchemeEditorContext.Provider value={value}>{children}</SchemeEditorContext.Provider>;
};
